import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { deviceTypeQueryKeys } from "@/shared/domain/deviceTypes/hooks/deviceTypeQueryKeys";

import { type Device } from "../Device";
import { type UpdateDeviceRequest } from "../DeviceService";

import { deviceQueryKeys } from "./deviceQueryKeys";
import { getQueryKey as getAllDevicesByOrgKey } from "./useAllDevicesByOrg";

interface UpdateDeviceParams {
  deviceId: string;
  resourceOwnerId: string;
  updates: UpdateDeviceRequest;
}

export function useUpdateDevice() {
  const { devices } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<Device, Error, UpdateDeviceParams>({
    mutationFn: async ({ deviceId, resourceOwnerId, updates }) => {
      return devices.updateDevice(deviceId, updates, {
        resourceOwnerId,
      });
    },
    onSuccess: (updatedDevice, { deviceId, resourceOwnerId, updates }) => {
      // A device changed, so invalidate every per-org list that contains it — the grid
      // and the all-devices selector roster. That way, wherever device properties are rendered,
      // their fresh values will be shown to the user.
      void queryClient.invalidateQueries({
        queryKey: deviceQueryKeys.lists(resourceOwnerId),
      });

      void queryClient.invalidateQueries({
        queryKey: getAllDevicesByOrgKey(resourceOwnerId),
      });

      // A device that gains, loses or changes its type changes the counts the
      // device types report; a tag or metadata edit does not.
      if ("device_type_id" in updates) {
        void queryClient.invalidateQueries({
          queryKey: deviceTypeQueryKeys.all(),
        });
      }

      // Refresh just this device's detail record in place — no refetch needed.
      queryClient.setQueryData(["device", deviceId], updatedDevice);
    },
  });
}
